import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Cloud, Shield, BarChart3, CheckCircle, Zap, Globe, Users } from 'lucide-react';
import './Home.css';

const services = [
  { icon: <Code size={28} />, title: 'Software Development', desc: 'Custom enterprise-grade applications built with modern architectures and cutting-edge technology.' },
  { icon: <Cloud size={28} />, title: 'Cloud Solutions', desc: 'Scalable cloud infrastructure, migration services, and managed cloud operations across AWS, Azure, GCP.' },
  { icon: <Shield size={28} />, title: 'Cybersecurity', desc: 'End-to-end security frameworks, penetration testing, compliance auditing, and threat intelligence.' },
  { icon: <BarChart3 size={28} />, title: 'IT Consulting', desc: 'Strategic technology roadmaps, digital transformation planning, and expert advisory services.' },
];

const stats = [
  { value: '200+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '50+', label: 'Expert Engineers' },
  { value: '12+', label: 'Years Experience' },
];

const whyUs = [
  'Dedicated project managers for every engagement',
  'Agile methodology with weekly deliverables',
  'Post-launch support and maintenance included',
  'NDA-protected development process',
  'Transparent pricing and no hidden costs',
  'ISO-compliant development standards',
];

export default function Home() {
  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-grid" />
          <div className="hero-glow" />
          <img
            src="https://images.unsplash.com/photo-1496065187959-7f07b8353c55?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Technology background"
            className="hero-img"
          />
          <div className="hero-overlay" />
        </div>
        <div className="container hero-content">
          <div className="hero-badge">
            <Zap size={12} />
            <span>Trusted Technology Partner</span>
          </div>
          <h1 className="hero-title">
            We Build<br />
            <span>Technology</span><br />
            That Matters
          </h1>
          <p className="hero-desc">
            GO Technology delivers bespoke software solutions, strategic IT consulting, and end-to-end digital transformation for forward-thinking enterprises.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="btn-primary">
              Start a Project <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="btn-outline">
              Our Services
            </Link>
          </div>
        </div>
        <div className="hero-scroll">
          <span>Scroll to explore</span>
          <div className="hero-scroll-line" />
        </div>
      </section>

      {/* STATS */}
      <section className="stats-bar">
        <div className="container">
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div className="stat-item" key={i}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="section about-intro">
        <div className="container about-intro-inner">
          <div className="about-intro-img">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
              alt="Team collaboration"
            />
            <div className="about-intro-badge">
              <Globe size={20} />
              <div>
                <strong>Global Reach</strong>
                <span>Serving clients worldwide</span>
              </div>
            </div>
          </div>
          <div className="about-intro-text">
            <span className="section-label">About GO Technology</span>
            <h2 className="section-title">Where <span>Innovation</span> Meets Precision</h2>
            <div className="gold-line" />
            <p className="section-desc" style={{ marginBottom: '1.5rem' }}>
              Founded on the belief that technology should empower — not complicate — GO Technology has been engineering transformative digital experiences for over a decade.
            </p>
            <p className="section-desc">
              We combine deep technical expertise with a consultancy mindset to deliver solutions that are scalable, secure, and strategically aligned with your business goals.
            </p>
            <Link to="/about" className="btn-outline" style={{ marginTop: '2rem' }}>
              Learn More <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What We Do</span>
            <h2 className="section-title">Our Core <span>Services</span></h2>
            <p className="section-desc">From ideation to deployment — comprehensive technology services tailored to accelerate your business.</p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <div className="service-card card" key={i}>
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link to="/services" className="service-link">
                  Learn more <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section why-us">
        <div className="container why-us-inner">
          <div>
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">The <span>GO Technology</span> Difference</h2>
            <div className="gold-line" />
            <p className="section-desc" style={{ marginBottom: '2.5rem' }}>
              We don't just write code — we partner with you to understand your vision, challenge assumptions, and build solutions that deliver measurable results.
            </p>
            <div className="why-list">
              {whyUs.map((item, i) => (
                <div className="why-item" key={i}>
                  <CheckCircle size={17} className="why-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="btn-primary" style={{ marginTop: '2.5rem' }}>
              Talk to an Expert <ArrowRight size={16} />
            </Link>
          </div>
          <div className="why-us-img">
            <img
              src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80"
              alt="Technology office"
            />
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section industries-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-label">Industries We Serve</span>
            <h2 className="section-title">Deep <span>Domain Expertise</span></h2>
          </div>
          <div className="industries-grid">
            {['FinTech', 'HealthTech', 'E-Commerce', 'EdTech', 'Manufacturing', 'Logistics', 'Real Estate', 'SaaS'].map((ind, i) => (
              <div className="industry-chip" key={i}>
                <Users size={14} />
                {ind}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container cta-inner">
          <div className="cta-glow" />
          <span className="section-label">Ready to Begin?</span>
          <h2 className="cta-title">Let's Build Something <span>Extraordinary</span></h2>
          <p className="cta-desc">Schedule a free consultation with our experts and discover how GO Technology can accelerate your business.</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn-primary">
              Get Free Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="btn-outline">View Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
